import { Button, Grid, Paper } from '@mui/material'
import { Person, RecordVoiceOver } from '@mui/icons-material'
import { Form, Formik, FormikHelpers } from 'formik'
import { inject } from 'mobx-react'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import AddressField from '../common/AddressField/AddressField'
import CardWithTitle from '../common/CardWithTitle/CardWithTitle'
import ESDataTableWithHeader from '../common/ESDataTableWithHeader'
import FullWidthField from '../common/FullWidthField'
import IsActiveToggle from '../common/IsActiveToggle'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import { noWhitespaceString } from '../common/YupExtensions'
import ContactEditDialog from '../contacts/ContactEditDialog'
import {
  DefaultContact,
  DefaultReferringDoctor,
  IContact,
  IReferringDoctor,
} from '../Definitions'
import { IContactDto } from '../generated_client'
import { IStores } from '../Stores'
import DataTableStore from '../stores/DataTableStore'
import * as Yup from 'yup'
import {
  createIReferringDoctorFormValues,
  IReferringDoctorFormValues,
  toIReferringDoctor,
} from './ReferringDoctorFormValues'
import _logic from './ReferringDoctorLogic'
import { Helmet } from 'react-helmet'

const ReferringDoctorSchema = Yup.object().shape({
  address: Yup.object({
    city: noWhitespaceString('City is required', true),
    line1: noWhitespaceString('Street address is required', true),
    line2: Yup.string(),
    state: noWhitespaceString('State is required', true),
    zip: noWhitespaceString('Zip code is required', true),
  }),
  firstName: noWhitespaceString('First name is required', true),
  lastName: noWhitespaceString('Last name is required', true),
  npi: Yup.string(),
  preferences: Yup.string(),
  suffix: Yup.string(),
})

interface IEditReferringDoctorProps {
  referringDoctorId?: string
  contactsDataTableStore?: DataTableStore<IContactDto, IContact>
  createContact?: (contact: IContact, title: string) => void
  getReferringDoctorById?: (id: string) => Promise<void>
  isLoading?: boolean
  referringDoctor?: IReferringDoctor
  referringDoctorEditFinished?: () => void
  rerouteToPath?: (path: string) => void
  saveReferringDoctor?: (referringDoctor: IReferringDoctor) => Promise<void>
  setSelectedReferringDoctor?: (referringDoctor: IReferringDoctor | undefined) => void
  getColumnSettingsAndContacts?: () => Promise<void> | undefined
}

interface IEditReferringDoctorState {
  reload: boolean
}
class EditReferringDoctor extends React.Component<
  IEditReferringDoctorProps,
  IEditReferringDoctorState
> {
  constructor(props: IEditReferringDoctorProps) {
    super(props)
    this.state = {
      reload: false,
    }
  }

  private async setup() {
    const { referringDoctorId } = this.props
    if (referringDoctorId) {
      await this.props.getReferringDoctorById!(referringDoctorId)
    } else {
      this.props.setSelectedReferringDoctor!(DefaultReferringDoctor(true))
    }
    await this.props.getColumnSettingsAndContacts!()
  }

  public async componentDidMount() {
    ;(window as any).formikRef = React.createRef()

    await this.setup()
  }

  public async componentDidUpdate(prevProps: IEditReferringDoctorProps) {
    if (this.props.referringDoctorId !== prevProps.referringDoctorId) {
      await this.setup()
    }
  }

  public componentWillUnmount() {
    this.props.setSelectedReferringDoctor!(undefined)
  }

  private cancel = () => {
    this.props.rerouteToPath!('/admin/referringDoctors')
  }

  private handleIsActiveChange =
    (
      values: IReferringDoctorFormValues,
      setFieldValue: (field: string, value: any) => void
    ) =>
    () => {
      setFieldValue('isActive', !values.isActive)
    }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  public save = async (
    values: IReferringDoctorFormValues,
    formikBag: FormikHelpers<IReferringDoctorFormValues>
  ) => {
    const referringDoctor = await toIReferringDoctor(values, this.props.referringDoctor)
    this.props.saveReferringDoctor!(referringDoctor).finally(() => {
      formikBag.setSubmitting(false)
      if (this.state.reload) {
        this.props.rerouteToPath!(
          `/admin/referringDoctors/referringDoctor/${referringDoctor.id}`
        )
        this.setup()
      } else {
        this.props.rerouteToPath!(`/admin/referringDoctors`)
      }
    })
  }

  private openDialog = () => {
    this.props.createContact!(DefaultContact(true), 'Add New Referring Doctor Contact')
  }

  public render() {
    const { contactsDataTableStore, referringDoctor } = this.props
    const initialValues = createIReferringDoctorFormValues(referringDoctor)
    const title =
      referringDoctor && referringDoctor.isNew
        ? 'Add New Referring Doctor'
        : 'Edit Referring Doctor'

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {referringDoctor ? (
          <Formik
            initialValues={initialValues}
            onSubmit={this.save}
            validationSchema={ReferringDoctorSchema}
            enableReinitialize={true}
            ref={(window as any).formikRef}
          >
            {({ isValid, values, setFieldValue, submitForm }) => (
              <Form>
                <ResponsiveAppBar title={title} pageIcon={<RecordVoiceOver />}>
                  <Grid container direction="row" spacing={2} justifyContent="flex-end">
                    <Grid item>
                      <Button
                        size="small"
                        variant="contained"
                        style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                        onClick={this.cancel}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                        disabled={!isValid}
                        onClick={this.handleSave(submitForm, true)}
                      >
                        Save
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                        disabled={!isValid}
                        onClick={this.handleSave(submitForm, false)}
                      >
                        Save and Close
                      </Button>
                    </Grid>
                  </Grid>
                </ResponsiveAppBar>
                <Grid
                  container
                  direction="row"
                  style={{ marginTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}
                >
                  <Grid container item xs={6} style={{ padding: 8 }}>
                    <CardWithTitle title="Referring Doctor" icon={RecordVoiceOver}>
                      <Grid item xs={12}>
                        <FullWidthField
                          variant="outlined"
                          type="outlined"
                          name="npi"
                          label="NPI"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FullWidthField
                          required
                          variant="outlined"
                          name="firstName"
                          label="First Name"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FullWidthField
                          required
                          variant="outlined"
                          name="lastName"
                          label="Last Name"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FullWidthField variant="outlined" name="suffix" label="Suffix" />
                      </Grid>
                      <Grid item xs={12}>
                        <AddressField
                          name={'address'}
                          label="Address"
                          showPhone={true}
                          showFax={true}
                          outlined={true}
                          required={true}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FullWidthField
                          name="preferences"
                          label="Preferences"
                          variant="outlined"
                          multiline
                          rows="3"
                        />
                      </Grid>
                      <Grid item>
                        <IsActiveToggle
                          isActive={values.isActive}
                          onChange={this.handleIsActiveChange(values, setFieldValue)}
                        />
                      </Grid>
                    </CardWithTitle>
                  </Grid>
                  {referringDoctor && !referringDoctor.isNew ? (
                    <Grid item xs={6}>
                      <Paper style={{ paddingTop: '16px' }}>
                        <ESDataTableWithHeader
                          dataTableManager={contactsDataTableStore!}
                          addButtonOnClick={this.openDialog}
                          elevation={0}
                          enableShowInactives
                          padding={0}
                          title="Contacts"
                          icon={Person}
                          rowsPerPage={5}
                        />
                      </Paper>
                      <ContactEditDialog contactVariant="ReferringDoctor" />
                    </Grid>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
        ) : (
          <></>
        )}
      </>
    )
  }
}

const InjectedEditReferringDoctor = inject<
  IStores,
  IEditReferringDoctorProps,
  Partial<IEditReferringDoctorProps>,
  any
>((stores: IStores) => ({
  contactsDataTableStore: stores.contacts.dataTableStore,
  createContact: stores.contacts.openDialogWithContact,
  getColumnSettingsAndContacts: stores.contacts.getColumnSettingsAndContacts,
  getReferringDoctorById: stores.referringDoctors.getReferringDoctorById,
  isLoading: stores.referringDoctors.isLoading,
  referringDoctor: stores.referringDoctors.selectedReferringDoctor,
  referringDoctorEditFinished: stores.referringDoctors.referringDoctorEditFinished,
  rerouteToPath: stores.global.rerouteToPath,
  saveReferringDoctor: stores.referringDoctors.saveReferringDoctor,
  setSelectedReferringDoctor: stores.referringDoctors.setSelectedReferringDoctor,
}))(EditReferringDoctor)

const EditReferringDoctorWithParams = (props: any) => {
  const params = useParams()
  return (
    <InjectedEditReferringDoctor
      {...props}
      referringDoctorId={params.referringDoctorId}
    />
  )
}

export default EditReferringDoctorWithParams
